import { useState } from "react";
import { Link, useSearchParams } from "react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Eye, EyeOff, CheckCircle2, AlertCircle, Clock } from "lucide-react";

import {
  mfaResetConfirmRequestSchema,
  type MfaResetConfirmRequest,
} from "@sendsms/shared";
import { api, ApiError } from "@/lib/api";
import {
  applyFieldErrors,
  translateApiError,
  translateFieldError,
} from "@/lib/form-errors";
import {
  Card,
  CardHeader,
  CardTitle,
  CardSubtitle,
  CardBody,
} from "@/components/ui/Card";
import { Input } from "@/components/ui/Input";
import { Button } from "@/components/ui/Button";
import { Alert } from "@/components/ui/Alert";

// MFA-RESET-UX-1 (21-Oct-2026): the link is verified on load, so the page shows one of three
// truths before asking for anything: the link is no longer valid (expired 48 h / used / cancelled),
// the link is inside the 24 h security window (exact local time it opens, cancel reminder), or the
// reset can be confirmed now (password form). The API keeps every security decision; the page only
// renders what the API already answers.
type LinkState =
  | { kind: "ready" }
  | { kind: "delay"; notBefore: number; expiresAt: number | null }
  | { kind: "invalid" };

function formatAt(epochSeconds: number, lang: string): string {
  const locale = lang.startsWith("ro") ? "ro-RO" : "en-GB";
  return new Date(epochSeconds * 1000).toLocaleString(locale, {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

const BACK_LINK =
  "inline-flex items-center gap-1.5 text-caption font-medium text-brand-500 hover:underline";
const PRIMARY_LINK =
  "inline-flex w-full items-center justify-center gap-2 rounded-pill bg-brand-500 px-6 py-3 text-body font-medium text-white transition-colors hover:bg-brand-600 focus:outline-none focus:shadow-focus";

export default function MfaResetConfirmPage() {
  const { t, i18n } = useTranslation();
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";

  const [showPassword, setShowPassword] = useState(false);
  const [topError, setTopError] = useState<string | null>(null);
  const [override, setOverride] = useState<LinkState | null>(null);
  const [success, setSuccess] = useState(false);

  const verify = useQuery({
    queryKey: ["mfa-reset-verify", token],
    queryFn: () => api.mfaResetVerify({ token }),
    enabled: token.length > 0,
    retry: false,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });

  const form = useForm<MfaResetConfirmRequest>({
    resolver: zodResolver(mfaResetConfirmRequestSchema),
    defaultValues: { token, password: "" },
    mode: "onSubmit",
  });

  const mutation = useMutation({
    mutationFn: (body: MfaResetConfirmRequest) => api.mfaResetConfirm(body),
    onSuccess: () => setSuccess(true),
    onError: (err: unknown) => {
      if (err instanceof ApiError) {
        if (err.code === "delay_window_active") {
          const raw = err.extra.seconds_remaining;
          const secs =
            err.retryAfterSeconds ?? (typeof raw === "number" ? raw : null);
          if (secs !== null) {
            setOverride({
              kind: "delay",
              notBefore: Math.floor(Date.now() / 1000) + secs,
              expiresAt: null,
            });
            return;
          }
        }
        if (err.code === "invalid_or_expired_token") {
          setOverride({ kind: "invalid" });
          return;
        }
        if (err.code === "invalid_credentials") {
          setTopError(t("auth.mfa_reset.confirm_bad_password"));
          return;
        }
      }
      const handled = applyFieldErrors(t, err, form.setError);
      if (!handled) setTopError(translateApiError(t, err));
    },
  });

  if (!token) {
    return (
      <Card>
        <CardHeader>
          <div className="mb-2 inline-flex h-12 w-12 items-center justify-center rounded-full bg-danger-50">
            <AlertCircle className="h-6 w-6 text-danger-500" />
          </div>
          <CardTitle>{t("errors.invalid_or_expired_token")}</CardTitle>
        </CardHeader>
      </Card>
    );
  }

  if (success) {
    return (
      <Card>
        <CardHeader>
          <div className="mb-2 inline-flex h-12 w-12 items-center justify-center rounded-full bg-success-50">
            <CheckCircle2 className="h-6 w-6 text-success-500" />
          </div>
          <CardTitle>{t("auth.mfa_reset.confirm_success_title")}</CardTitle>
          <CardSubtitle>
            {t("auth.mfa_reset.confirm_success_message")}
          </CardSubtitle>
        </CardHeader>
        <CardBody>
          <Link to="/login" className={PRIMARY_LINK}>
            {t("auth.reset_password.sign_in_now")}
          </Link>
        </CardBody>
      </Card>
    );
  }

  let link: LinkState | null = override;
  if (!link && verify.data) {
    link = verify.data.in_delay_window
      ? {
          kind: "delay",
          notBefore: verify.data.not_before,
          expiresAt: verify.data.expires_at,
        }
      : { kind: "ready" };
  }
  const verifyInvalid =
    verify.error instanceof ApiError && verify.error.status === 400;

  if ((link && link.kind === "invalid") || verifyInvalid) {
    return (
      <Card>
        <CardHeader>
          <div className="mb-2 inline-flex h-12 w-12 items-center justify-center rounded-full bg-danger-50">
            <AlertCircle className="h-6 w-6 text-danger-500" />
          </div>
          <CardTitle>{t("auth.mfa_reset.link_invalid_title")}</CardTitle>
          <CardSubtitle>{t("auth.mfa_reset.link_invalid_message")}</CardSubtitle>
        </CardHeader>
        <CardBody>
          <Link to="/mfa/reset-request" className={PRIMARY_LINK}>
            {t("auth.mfa_reset.link_invalid_cta")}
          </Link>
          <div className="mt-4">
            <Link to="/login" className={BACK_LINK}>
              {t("auth.forgot_password.back_to_login")}
            </Link>
          </div>
        </CardBody>
      </Card>
    );
  }

  if (!link && verify.isPending) {
    return (
      <Card>
        <CardHeader>
          <div className="mb-2 flex h-12 w-12 items-center justify-center">
            <div
              className="h-8 w-8 animate-spin rounded-full border-2 border-border border-t-brand-500"
              aria-hidden="true"
            />
          </div>
          <CardTitle>{t("common.loading")}</CardTitle>
        </CardHeader>
      </Card>
    );
  }

  if (!link) {
    return (
      <Card>
        <CardHeader>
          <div className="mb-2 inline-flex h-12 w-12 items-center justify-center rounded-full bg-danger-50">
            <AlertCircle className="h-6 w-6 text-danger-500" />
          </div>
          <CardTitle>{t("auth.mfa_reset.verify_failed")}</CardTitle>
        </CardHeader>
        <CardBody>
          <Link to="/login" className={BACK_LINK}>
            {t("auth.forgot_password.back_to_login")}
          </Link>
        </CardBody>
      </Card>
    );
  }

  if (link.kind === "delay") {
    const opensAt = formatAt(link.notBefore, i18n.language);
    const expiresAt =
      link.expiresAt !== null ? formatAt(link.expiresAt, i18n.language) : null;
    return (
      <Card>
        <CardHeader>
          <div className="mb-2 inline-flex h-12 w-12 items-center justify-center rounded-full bg-brand-50">
            <Clock className="h-6 w-6 text-brand-500" />
          </div>
          <CardTitle>{t("auth.mfa_reset.confirm_title")}</CardTitle>
          <CardSubtitle>
            {t("auth.mfa_reset.delay_title", { at: opensAt })}
          </CardSubtitle>
        </CardHeader>
        <CardBody>
          <Alert tone="info" description={t("auth.mfa_reset.delay_message")} />
          <p className="mt-4 text-caption text-ink-muted">
            {t("auth.mfa_reset.delay_hint")}
            {expiresAt
              ? " " + t("auth.mfa_reset.delay_expires", { at: expiresAt })
              : null}
          </p>
          <div className="mt-4">
            <Link to="/login" className={BACK_LINK}>
              {t("auth.forgot_password.back_to_login")}
            </Link>
          </div>
        </CardBody>
      </Card>
    );
  }

  const passwordError = form.formState.errors.password?.message
    ? translateFieldError(t, form.formState.errors.password.message)
    : undefined;

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("auth.mfa_reset.confirm_title")}</CardTitle>
        <CardSubtitle>{t("auth.mfa_reset.confirm_subtitle")}</CardSubtitle>
      </CardHeader>

      <CardBody>
        {topError ? (
          <Alert tone="danger" description={topError} className="mb-5" />
        ) : null}

        <form
          onSubmit={form.handleSubmit((data) => {
            setTopError(null);
            mutation.mutate(data);
          })}
          className="space-y-4"
          noValidate
        >
          <Input
            label={t("auth.mfa_reset.confirm_password_label")}
            type={showPassword ? "text" : "password"}
            autoComplete="current-password"
            autoFocus
            required
            {...form.register("password")}
            error={passwordError}
            trailingElement={
              <button
                type="button"
                onClick={() => setShowPassword((s) => !s)}
                aria-label={showPassword ? t("common.hide") : t("common.show")}
                className="flex h-8 w-8 items-center justify-center rounded-md text-ink-subtle transition-colors hover:bg-surface-muted focus:outline-none focus-visible:shadow-focus"
              >
                {showPassword ? (
                  <EyeOff className="h-4 w-4" />
                ) : (
                  <Eye className="h-4 w-4" />
                )}
              </button>
            }
          />

          <Button
            type="submit"
            fullWidth
            size="lg"
            isLoading={mutation.isPending}
          >
            {t("auth.mfa_reset.confirm_submit")}
          </Button>
        </form>
      </CardBody>
    </Card>
  );
}